import type { AdventureBoxReward, AdventureRewardType } from "../adventure/adventure_box_rewards.js";

// Pure Prize Shot rules: the service persists the round and grants the rewards.

export const PRIZE_SHOT_COLUMNS = 4;
export const PRIZE_SHOT_ROWS = 5;
export const PRIZE_SHOT_CELLS = PRIZE_SHOT_COLUMNS * PRIZE_SHOT_ROWS;

const PLAIN_WEIGHT = 1;
const GRAND_WEIGHT_START = 0.05;
const GRAND_WEIGHT_GROWTH = 4;

const REWARD_TYPES: readonly AdventureRewardType[] = [
  "coin",
  "heart",
  "infinite_hearts",
  "no_ads",
  "fireworks",
  "multiballs",
  "big_balls",
  "infinite_balls",
  "mines",
];

export type PrizeShotReward = AdventureBoxReward & { grand?: boolean };

export interface PrizeShotContinue
{
  price: number;
  shots: number;
  /** 0 is no limit. */
  max_purchases: number;
}

export interface PrizeShotConfig
{
  enabled: boolean;
  period_hours: number;
  /** Start of the very first period. */
  anchor: string;
  /** Free balls a round starts with. */
  shots: number;
  prizes: PrizeShotReward[];
  grand_prize: PrizeShotReward | null;
  continue: PrizeShotContinue;
}

export interface PrizeShotRound
{
  /** ISO start of the period. */
  round_id: string;
  ends_at_ms: number;
  /** Row-major from the top-left; null is an empty balloon. */
  board: ( PrizeShotReward | null )[];
  /** Popped cells in the order they were hit. */
  popped: number[];
  shots_left: number;
  continues: number;
  /** How many of `popped` are already paid out. */
  claimed_count: number;
  board_no: number;
}

export const DEFAULT_PRIZE_SHOT_CONFIG: PrizeShotConfig = {
  enabled: true,
  period_hours: 48,
  anchor: "2026-01-01T00:00:00.000Z",
  shots: 3,
  prizes: Array.from( { length: 9 }, () => ( { type: "coin" as const, amount: 100 } ) ),
  grand_prize: { type: "big_balls", amount: 5 },
  continue: { price: 600, shots: 3, max_purchases: 0 },
};

function asRecord( value: unknown ): Record<string, unknown>
{
  if ( value && typeof value === "object" && !Array.isArray( value ) )
    return value as Record<string, unknown>;

  return {};
}

function positiveInt( value: unknown, fallback: number, min = 1 ): number
{
  const n = Number( value );
  if ( !Number.isFinite( n ) || n < min )
    return fallback;

  return Math.floor( n );
}

function isReward( reward: PrizeShotReward | null ): reward is PrizeShotReward
{
  return reward !== null;
}

export function normalizeReward( raw: unknown ): PrizeShotReward | null
{
  const value = asRecord( raw );
  const type = value.type as AdventureRewardType;
  const amount = Number( value.amount );

  if ( !REWARD_TYPES.includes( type ) || !Number.isFinite( amount ) || amount <= 0 )
    return null;

  return { type, amount: Math.floor( amount ) };
}

function normalizeAnchor( value: unknown, fallback: string ): string
{
  if ( typeof value !== "string" || !Number.isFinite( Date.parse( value ) ) )
    return fallback;

  return value;
}

function normalizePrizes( value: unknown, fallback: PrizeShotReward[] ): PrizeShotReward[]
{
  if ( !Array.isArray( value ) )
    return fallback;

  // One cell stays for the grand prize.
  const prizes = value
    .map( normalizeReward )
    .filter( isReward )
    .slice( 0, PRIZE_SHOT_CELLS - 1 );

  return prizes.length > 0 ? prizes : fallback;
}

function normalizeGrandPrize( value: Record<string, unknown>, fallback: PrizeShotReward | null ): PrizeShotReward | null
{
  if ( value.grand_prize === null )
    return null;
  if ( !( "grand_prize" in value ) )
    return fallback;

  return normalizeReward( value.grand_prize ) ?? fallback;
}

/** Server content-config → config; anything missing or mistyped keeps the bundled value. */
export function normalizePrizeShotConfig( raw: unknown ): PrizeShotConfig
{
  const value = asRecord( raw );
  const defaults = DEFAULT_PRIZE_SHOT_CONFIG;
  const continue_value = asRecord( value.continue );

  return {
    enabled: typeof value.enabled === "boolean" ? value.enabled : defaults.enabled,
    period_hours: positiveInt( value.period_hours, defaults.period_hours ),
    anchor: normalizeAnchor( value.anchor, defaults.anchor ),
    shots: positiveInt( value.shots, defaults.shots ),
    prizes: normalizePrizes( value.prizes, defaults.prizes ),
    grand_prize: normalizeGrandPrize( value, defaults.grand_prize ),
    continue: {
      price: positiveInt( continue_value.price, defaults.continue.price, 0 ),
      shots: positiveInt( continue_value.shots, defaults.continue.shots ),
      max_purchases: positiveInt( continue_value.max_purchases, defaults.continue.max_purchases, 0 ),
    },
  };
}

export function prizeShotPeriod( config: PrizeShotConfig, now_ms: number ): { start_ms: number; end_ms: number }
{
  const anchor = Date.parse( config.anchor );
  const length = config.period_hours * 3_600_000;
  const index = Math.floor( ( now_ms - anchor ) / length );
  const start_ms = anchor + index * length;

  return { start_ms, end_ms: start_ms + length };
}

/** mulberry32: the same seed always deals the same board. */
function seededRandom( seed: number ): () => number
{
  let a = seed >>> 0;

  return () =>
  {
    a = ( a + 0x6D2B79F5 ) >>> 0;
    let t = a;
    t = Math.imul( t ^ ( t >>> 15 ), t | 1 );
    t ^= t + Math.imul( t ^ ( t >>> 7 ), t | 61 );
    return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
  };
}

export function dealBoard( prizes: readonly PrizeShotReward[], seed: number, grand_prize: PrizeShotReward | null = null ): ( PrizeShotReward | null )[]
{
  const all: PrizeShotReward[] = prizes.map( prize => ( { type: prize.type, amount: prize.amount } ) );
  if ( grand_prize )
    all.push( { type: grand_prize.type, amount: grand_prize.amount, grand: true } );

  const board: ( PrizeShotReward | null )[] = Array.from( { length: PRIZE_SHOT_CELLS }, ( _, i ) => all[i] ?? null );
  const random = seededRandom( seed );

  for ( let i = board.length - 1; i > 0; i-- )
  {
    const j = Math.floor( random() * ( i + 1 ) );
    [ board[i], board[j] ] = [ board[j], board[i] ];
  }

  return board;
}

/** `carried_shots`: balls the previous board left unused. */
export function createRound( config: PrizeShotConfig, now_ms: number, seed: number, carried_shots = 0 ): PrizeShotRound
{
  const period = prizeShotPeriod( config, now_ms );

  return {
    round_id: new Date( period.start_ms ).toISOString(),
    ends_at_ms: period.end_ms,
    board: dealBoard( config.prizes, seed, config.grand_prize ),
    popped: [],
    shots_left: config.shots + Math.max( 0, Math.floor( carried_shots ) ),
    continues: 0,
    claimed_count: 0,
    board_no: 0,
  };
}

/** A round saved with the old `claimed: boolean` instead of `claimed_count`. */
export function migrateRound( round: PrizeShotRound & { claimed?: boolean } ): PrizeShotRound
{
  if ( typeof round.claimed_count !== "number" )
    round.claimed_count = round.claimed ? round.popped.length : 0;
  if ( typeof round.board_no !== "number" )
    round.board_no = 0;

  delete round.claimed;
  return round;
}

export function isPopped( round: PrizeShotRound, cell: number ): boolean
{
  return round.popped.includes( cell );
}

/** Port of the game designer's weightedChoiceIndex; `random` returns [0, 1). */
export function weightedChoiceIndex( weights: readonly number[], random: () => number = Math.random ): number
{
  const sum = weights.reduce( ( total, weight ) => total + weight, 0 );
  let d = random() * sum;

  for ( let i = 0; i < weights.length - 1; i++ )
  {
    d -= weights[i];
    if ( d <= 0 )
      return i;
  }

  return weights.length - 1;
}

/** Rare on a full board, more and more likely towards the last balloons. */
export function grandPrizeWeight( popped: number, cells: number ): number
{
  const progress = cells > 1 ? popped / ( cells - 1 ) : 1;
  return Math.max( GRAND_WEIGHT_START, GRAND_WEIGHT_GROWTH * progress * progress );
}

/**
 * What a balloon holds is settled when it pops: a weighted pick among everything
 * still hidden is swapped into this cell. Undefined when it can't be popped.
 */
export function popCell( round: PrizeShotRound, cell: number, random: () => number = Math.random ): PrizeShotReward | null | undefined
{
  if ( !Number.isInteger( cell ) || cell < 0 || cell >= round.board.length || isPopped( round, cell ) )
    return undefined;

  const hidden = round.board
    .map( ( _, i ) => i )
    .filter( i => !isPopped( round, i ) );
  const grand_weight = grandPrizeWeight( round.popped.length, round.board.length );
  const weights = hidden.map( i => round.board[i]?.grand ? grand_weight : PLAIN_WEIGHT );
  const source = hidden[weightedChoiceIndex( weights, random )];

  [ round.board[cell], round.board[source] ] = [ round.board[source], round.board[cell] ];
  round.popped.push( cell );

  return round.board[cell];
}

export function prizesLeft( round: PrizeShotRound ): number
{
  return round.board.filter( ( reward, cell ) => reward !== null && !isPopped( round, cell ) ).length;
}

/** A fresh board for the same round; the balls left stay. */
export function dealNextBoard( round: PrizeShotRound, config: PrizeShotConfig, seed: number ): void
{
  round.board = dealBoard( config.prizes, seed, config.grand_prize );
  round.popped = [];
  round.claimed_count = 0;
  round.continues = 0;
  round.board_no++;
}

export function wonRewards( round: PrizeShotRound ): PrizeShotReward[]
{
  return round.popped
    .map( cell => round.board[cell] )
    .filter( isReward );
}

export function unclaimedRewards( round: PrizeShotRound ): PrizeShotReward[]
{
  return round.popped
    .slice( round.claimed_count )
    .map( cell => round.board[cell] )
    .filter( isReward );
}

/** Merged by type. */
export function sumRewards( rewards: readonly PrizeShotReward[] ): PrizeShotReward[]
{
  const totals = new Map<AdventureRewardType, number>();
  for ( const reward of rewards )
    totals.set( reward.type, ( totals.get( reward.type ) ?? 0 ) + reward.amount );

  return [ ...totals ].map( ( [ type, amount ] ) => ( { type, amount } ) );
}

/** Every prize is out or every balloon is popped. */
export function isBoardCleared( round: PrizeShotRound ): boolean
{
  if ( round.popped.length >= round.board.length )
    return true;

  return round.board.every( ( reward, cell ) => reward === null || isPopped( round, cell ) );
}

/** Time to take what was found: the balls are used up, or every prize is out. */
export function isBatchOver( round: PrizeShotRound ): boolean
{
  return round.shots_left <= 0 || isBoardCleared( round );
}

export function canContinue( round: PrizeShotRound, config: PrizeShotConfig ): boolean
{
  if ( isBoardCleared( round ) )
    return false;

  const max_purchases = config.continue.max_purchases;
  return max_purchases === 0 || round.continues < max_purchases;
}
